import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { of, throwError } from 'rxjs';
import { timeout, catchError, tap } from 'rxjs/operators';
import { LocalStorageService } from './local-storage/local-storage.service';
import { CacheService } from './cache/cache.service';

@Injectable({
  providedIn: 'root'
})
export class SearchGlobalService {
  private readonly API_PATH = '/api';
  private readonly VIP_PATH = '/vip';
  private readonly BUSCADOR_VERSION = '/v1/buscador';

  // TTL de 10 minutos - se invalida automaticamente al editar via ABM
  private readonly SEARCH_CACHE_TTL = 10 * 60 * 1000;

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private cacheService: CacheService,
  ) { }

  /**
   * Determina si el usuario está autenticado con VIP (Oidc)
   * Esto afecta qué URL base se usa para operaciones de lectura (GET)
   */
  get isAuthVip(): boolean {
    return this.localStorageService.getIsVip;
  }

  /**
   * Obtiene el ambiente (env) según el tipo de flujo y configuración
   * - Si el menuApp actual es VIP (título), usa VIP
   * - Por defecto, DM
   */
  private getEnv(): string {
    // Verificar si el menú actual es VIP por su título
    if (this.localStorageService.isMenuVip()) {
      return this.localStorageService.GPC_CONFIG.env.vip;
    }

    // Por defecto, interno
    return this.localStorageService.GPC_CONFIG.env.interno;
  }

  /**
   * Obtiene la configuración (config) por defecto.
   * En ambiente local retorna "Default" (para mantener compatibilidad con mocks)
   * En otros ambientes retorna "BUSCADOR_Default"
   */
  private getDefaultConfig(): string {
    const baseConfig = this.localStorageService.GPC_CONFIG.config.default;

    // En local, usar config simple para mantener compatibilidad con mocks existentes
    if (String(environment.IS_LOCAL.toLowerCase()) === "true") {
      return baseConfig;
    }

    return `SEARCH_${baseConfig}`;
  }

  /**
   * Construye la URL base para consultas (GET) considerando el flujo de autenticación
   * AUTENTICACIÓN VIP (Oidc): {F_VIP_URL_BASE_API}/vip/gcp-service/v1/buscador
   * AUTENTICACIÓN DM (PingID): {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/buscador
   *
   * NOTA: El environment (VIP/DM) en el path se determina por el menú/config que se consulta,
   * no por el tipo de autenticación
   */
  private buildBaseUrlForRead(): string {
    const urlBaseApi = this.isAuthVip
      ? this.localStorageService.configInit["F_VIP_URL_BASE_API"]
      : this.localStorageService.configInit["F_PINGID_URL_BASE_API"];

    if (this.isAuthVip) {
      // Para usuarios autenticados con VIP (Oidc): extraer solo "gcp-service" de "facade-im/gcp-service"
      const fullMicroservicio = this.localStorageService.configInit["F_URL_MS_IM_GCP"];
      const gcpService = fullMicroservicio ? fullMicroservicio.split('/').pop() : "";
        return `${urlBaseApi}${this.VIP_PATH}/${gcpService}${this.BUSCADOR_VERSION}`;
    } else {
      // Para usuarios autenticados con Interno (PingID): usar la ruta completa "facade-im/gcp-service"
      const rutaMicroservicio = this.localStorageService.configInit["F_URL_MS_IM_GCP"];

      const microservicioPath = rutaMicroservicio ? `/${rutaMicroservicio}` : "";
      return `${urlBaseApi}${microservicioPath}${this.API_PATH}${this.BUSCADOR_VERSION}`;
    }
  }

  /**
   * Construye la URL base para operaciones de escritura (POST, DELETE)
   * Siempre usa PINGID (interno), independientemente de si el usuario está en VIP
   * {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/buscador
   */
  private buildBaseUrlForWrite(): string {
    const rutaMicroservicio = (environment.MICROSERVICIO.toString().toLowerCase() === "true")
      ? this.localStorageService.configInit["F_URL_MS_IM_GCP"]
      : "";

    const microservicioPath = rutaMicroservicio ? `/${rutaMicroservicio}` : "";
    return `${this.localStorageService.configInit["F_PINGID_URL_BASE_API"]}${microservicioPath}${this.API_PATH}${this.BUSCADOR_VERSION}`;
  }

  /**
   * Consulta Search Global
   * Método GET
   *
   * Ejemplos de URLs generadas:
   * - Usuario Interno consultando buscador INTERNO (DM):
   *   {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/buscador/AR/DM/BUSCADOR_DEFAULT
   *
   * - Usuario Interno consultando buscador VIP:
   *   {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/buscador/AR/VIP/BUSCADOR_DEFAULT
   *
   * - Usuario VIP consultando buscador VIP:
   *   {F_VIP_URL_BASE_API}/vip/gcp-service/v1/buscador/AR/VIP/BUSCADOR_DEFAULT
   *
   * NOTA: El parámetro 'type' ya contiene el environment (VIP/DM)
   */
  getSearchGlobal(country: string, type: string, method: string) {
    const config = this.getDefaultConfig();

    const cacheKey = `search-global|${country}|${type}|${config}`;
    const cached = this.cacheService.get(cacheKey);
    if (cached) {
      return of(cached);
    }

    const baseUrl = this.buildBaseUrlForRead();
    const url = `${baseUrl}/${country}/${type}/${config}`;
    console.log("getSearchGlobal URL------>", url);

    return this.http.get<any>(url)
      .pipe(
        timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
        tap(response => {
          if (!response?.error) {
            this.cacheService.set(cacheKey, response, this.SEARCH_CACHE_TTL);
          }
        }),
        catchError(err => of({error: true, err, payload: {error: []}}))
      );
  }

  /**
   * Eliminar Search Global
   * Método DELETE (siempre usa interno/PINGID para escritura)
   *
   * Ejemplos:
   * - Eliminar de buscador VIP desde usuario Interno (DM):
   *   {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/buscador/AR/VIP/BUSCADOR_DEFAULT/items/{id}
   *
   * NOTA: El parámetro 'type' ya contiene el environment (VIP/DM)
   */
  deleteSearhcGlobal(id: string, country: string, type: string, method: string) {
    const config = this.getDefaultConfig();

    if (String(environment.IS_LOCAL.toLowerCase()) === "true") {
      // Modo local: usar mock - retornar respuesta simulada
      const mockResponse = { success: true, message: 'Mock: Elemento eliminado correctamente' };
      return of(mockResponse)
        .pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)), catchError(err => of({error: true, err, payload: {error: []}})));
    }

    const baseUrl = this.buildBaseUrlForWrite();
    const url = `${baseUrl}/${country}/${type}/${config}/items/${id}`;
    console.log("deleteSearhcGlobal URL------>", url);

    return this.http.delete(url).pipe(
      timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
      tap(() => this.invalidateSearchGlobalCache()),
      catchError(err => of({error: true, err, payload: {error: []}}))
    );
  }

  /**
   * Carga Search Global
   * Método POST (siempre usa interno/PINGID para escritura)
   *
   * Ejemplos:
   * - Carga buscador VIP desde usuario Interno:
   *   {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/buscador/charge/AR/VIP/BUSCADOR_DEFAULT
   *
   * - Carga buscador INTERNO (DM) desde usuario Interno:
   *   {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/buscador/charge/AR/DM/BUSCADOR_DEFAULT
   *
   * NOTA: El parámetro 'type' ya contiene el environment (VIP/DM)
   */
  postSearchGlobal(body: any, country: string, type: string, method: string) {
    const config = this.getDefaultConfig();

    if (String(environment.IS_LOCAL.toLowerCase()) === "true") {

      const mockResponse = { success: true, message: 'Mock: Datos cargados correctamente' };
      return of(mockResponse)
        .pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)), catchError(err => of({error: true, err, payload: {error: []}})));
    }

    const baseUrl = this.buildBaseUrlForWrite();
    const url = `${baseUrl}/charge/${country}/${type}/${config}`;

    return this.http.post(url, body).pipe(
      timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
      tap(() => this.invalidateSearchGlobalCache()),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  /** Invalida todo el cache de search global */
  invalidateSearchGlobalCache(): void {
    this.cacheService.invalidateByPrefix('search-global|');
  }

}
